import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { RiShareForwardFill } from 'react-icons/ri';

import SocketService from '@src/services/SocketService';

import { useCalculatorActions } from '@src/hooks';

import { useRoomContext } from '@src/contexts';

import { useCalculatorStore, useNotificationStore, usePeersStore, useChatStore, useDrawerStore } from '@src/stores';

import { ECommonSocketKeys, EMessageType, IMessage, TCalculatorMode, ECalculatorSocketEvents } from '@src/types';
import { createIcon, getRoomId } from '@src/helpers';

type TProps = {
  mode: TCalculatorMode;
  isReadOnly: boolean;
};

const useCalculatorHeader = ({ mode, isReadOnly }: TProps) => {
  const {
    sharedCalculatorData: { isOpen: isSharedCalculatorOpened, owner, settings, isSharedByMe },
    setIsPersonalCalculatorVisible,
    calculatorState,
    sharedCalculatorState,
  } = useCalculatorStore(
    useShallow((state) => ({
      sharedCalculatorData: state.sharedCalculatorData,
      setIsPersonalCalculatorVisible: state.setIsPersonalCalculatorVisible,
      calculatorState: state.calculatorState,
      sharedCalculatorState: state.sharedCalculatorState,
    }))
  );

  const { currentPeer, canUserShareCalculator, isPublicChatEnabled } = useRoomContext();

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  const { appendMessage, setSelectedReceiverData } = useChatStore(
    useShallow((state) => ({
      appendMessage: state.appendMessage,
      setSelectedReceiverData: state.setSelectedReceiverData,
    }))
  );

  const { setSelectedKey } = useDrawerStore(
    useShallow((state) => ({
      setSelectedKey: state.setSelectedKey,
    }))
  );

  // Determine which state to use based on whether this is a shared or personal calculator
  const activeState = isReadOnly ? sharedCalculatorState : calculatorState;
  const { expression, result, error } = activeState;

  const { handleShareCalculator: shareCalculator, handleStopShareCalculator } = useCalculatorActions();

  const isConverterZero = useMemo(() => {
    if (mode === 'converter') {
      if (!expression || !result) return true;
      return parseFloat(String(expression)) === 0 || parseFloat(String(result)) === 0;
    }
    return false;
  }, [mode, expression, result]);

  const canShareResultInPublicChat = useMemo(() => {
    if (mode === 'converter') {
      return !error && !isConverterZero && isPublicChatEnabled;
    }

    return !!expression && !error && !/^[0-9.]+$/.test(expression) && isPublicChatEnabled;
  }, [expression, error, isConverterZero, isPublicChatEnabled, mode]);

  const handleShareCalculator = useCallback(async () => {
    if (!canUserShareCalculator || !currentPeer?.peer_info?.peer_uuid) return;

    await shareCalculator({
      mode,
      currentPeer: {
        peer_name: currentPeer.peer_info?.peer_name || '',
        peer_id: currentPeer.id,
        peer_uuid: currentPeer.peer_info?.peer_uuid || '',
      },
    });

    SocketService.emit(ECalculatorSocketEvents.CALCULATOR_OPERATIONS_SYNC, {
      calculatorOperation: {
        action: 'sync',
        actionButton: '',
        expression: expression ?? '',
        result: result ?? '',
        peer_info: {
          peer_id: currentPeer.id ?? '',
          peer_uuid: currentPeer.peer_info?.peer_uuid ?? '',
          peer_name: currentPeer.peer_name ?? '',
          peer_record_id: currentPeer.record_id ?? '',
        },
      },
    });
  }, [canUserShareCalculator, currentPeer, mode, shareCalculator, expression, result]);

  const handleShareResultInPublicChat = useCallback(() => {
    if (!isPublicChatEnabled) return;

    const currentPeer = usePeersStore.getState().peers.get(SocketService.getSocketId() || '');

    // Format the message with the current operation and result
    const messageText = `${expression} = ${result}`;

    // Create message data object with calculator marker
    const messageData: IMessage = {
      room_id: getRoomId(),
      peer_id: currentPeer?.id || '',
      peer_name: currentPeer?.peer_info?.peer_name || '',
      to_peer_id: '',
      to_peer_name: '',
      peer_msg: messageText,
      timestamp: new Date().toISOString(),
      originalTimestamp: new Date().toISOString(),
      chatMessageId: Math.random().toString(36).substring(2, 15),
      isShown: false,
      peer_record_id: currentPeer?.record_id || '',
      to_peer_record_id: '',
      file: null,
      type: EMessageType.PUBLIC,
      // Add custom metadata to identify calculator messages and mode
      metadata: {
        source: 'calculator',
        mode,
      },
    };

    SocketService.emit(ECommonSocketKeys.MESSAGE, messageData);

    appendMessage(messageData);

    setSelectedReceiverData({ key: EMessageType.PUBLIC, value: '' });
    setSelectedKey('chat', true);

    appendNotification({
      text: 'Successfully shared calculator results in the public chat',
      icon: createIcon({ icon: RiShareForwardFill, size: 14 }),
    });
  }, [
    expression,
    appendMessage,
    setSelectedKey,
    appendNotification,
    result,
    mode,
    setSelectedReceiverData,
    isPublicChatEnabled,
  ]);

  const handleCloseCalculator = useCallback(async () => {
    setIsPersonalCalculatorVisible(false);

    if (isSharedByMe) {
      await handleStopShareCalculator();
    }
  }, [handleStopShareCalculator, isSharedByMe, setIsPersonalCalculatorVisible]);

  return {
    isSharedByMe,
    handleShareCalculator,
    handleShareResultInPublicChat,
    handleCloseCalculator,
    handleStopShareCalculator,
    isSharedCalculatorOpened,
    sharedCalculatorOwner: owner,
    sharedCalculatorSettings: settings,
    canUserShareCalculator,
    isPublicChatEnabled,
    calculatorState: activeState,
    canShareResultInPublicChat,
  };
};

export default useCalculatorHeader;
